import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { projectAPI, taskAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { Upload, Plus, FileSpreadsheet, Trash2, Edit, AlertCircle, Palette, Flag, ChevronDown, ChevronRight, List, Layers, Search, X } from 'lucide-react';

// Predefined colors for tasks
const TASK_COLORS = [
  { name: 'Orange', value: '#f97316' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Teal', value: '#14b8a6' },
];

export default function WBSManager({ projectId, tasks, milestones = [], onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('flat'); // 'flat' or 'milestone'
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [newTask, setNewTask] = useState({
    name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    progress: 0,
    parent_id: '',
    milestone_id: '',
    dependencies: [],
    color: '#f97316',
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const validTypes = ['.csv', '.xlsx', '.xls'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(ext)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setUploading(true);
    try {
      const res = await projectAPI.importWBS(projectId, file);
      toast.success(res.data.message);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import WBS');
    } finally {
      setUploading(false);
    }
  }, [projectId, onRefresh]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.name.trim()) {
      toast.error('Task name is required');
      return;
    }

    setLoading(true);
    try {
      await taskAPI.create(projectId, {
        ...newTask,
        parent_id: newTask.parent_id || null,
        milestone_id: newTask.milestone_id || null,
      });
      setAddOpen(false);
      setNewTask({
        name: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        progress: 0,
        parent_id: '',
        milestone_id: '',
        dependencies: [],
        color: '#f97316',
      });
      onRefresh();
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    if (!editTask.name.trim()) {
      toast.error('Task name is required');
      return;
    }

    setLoading(true);
    try {
      await taskAPI.update(projectId, editTask.id, {
        name: editTask.name,
        start_date: editTask.start_date,
        end_date: editTask.end_date,
        progress: editTask.progress,
        parent_id: editTask.parent_id || null,
        milestone_id: editTask.milestone_id || null,
        dependencies: editTask.dependencies || [],
        color: editTask.color || '#f97316',
      });
      setEditTask(null);
      onRefresh();
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;

    try {
      await taskAPI.delete(projectId, taskId);
      onRefresh();
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const toggleDependency = (taskId, currentTask, setCurrentTask) => {
    const deps = currentTask.dependencies || [];
    if (deps.includes(taskId)) {
      setCurrentTask({ ...currentTask, dependencies: deps.filter(d => d !== taskId) });
    } else {
      setCurrentTask({ ...currentTask, dependencies: [...deps, taskId] });
    }
  };

  const rootTasks = tasks.filter(t => !t.parent_id);
  const getSubtasks = (parentId) => tasks.filter(t => t.parent_id === parentId);
  
  // Search filtering
  const filteredTasks = searchQuery.trim()
    ? tasks.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : tasks;
  
  const filteredRootTasks = searchQuery.trim()
    ? filteredTasks.filter(t => !t.parent_id || filteredTasks.some(ft => ft.id === t.parent_id))
    : rootTasks;
  
  // Milestone-grouped view helpers (with search filtering)
  const getTasksByMilestone = (milestoneId) => filteredTasks.filter(t => t.milestone_id === milestoneId);
  const unassignedTasks = filteredTasks.filter(t => !t.milestone_id);
  
  const toggleMilestoneExpand = (milestoneId) => {
    setExpandedMilestones(prev => ({
      ...prev,
      [milestoneId]: !prev[milestoneId]
    }));
  };
  
  // Initialize all milestones as expanded
  React.useEffect(() => {
    const initial = {};
    milestones.forEach(m => { initial[m.id] = true; });
    initial['unassigned'] = true;
    setExpandedMilestones(initial);
  }, [milestones]);

  const renderTaskRow = (task, level = 0) => {
    const subtasks = getSubtasks(task.id);
    const parentTask = task.parent_id ? tasks.find(t => t.id === task.parent_id) : null;
    const depTasks = task.dependencies?.map(depId => tasks.find(t => t.id === depId)).filter(Boolean) || [];
    const milestone = task.milestone_id ? milestones.find(m => m.id === task.milestone_id) : null;

    return (
      <React.Fragment key={task.id}>
        <TableRow className="hover:bg-zinc-50" data-testid={`wbs-row-${task.id}`}>
          <TableCell className="font-medium">
            <span style={{ paddingLeft: level * 20 }} className="flex items-center gap-2">
              {level > 0 && <span className="text-zinc-400">└</span>}
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: task.color || '#f97316' }}
              />
              {task.name}
            </span>
          </TableCell>
          <TableCell>{formatDate(task.start_date)}</TableCell>
          <TableCell>{formatDate(task.end_date)}</TableCell>
          <TableCell>{task.progress}%</TableCell>
          <TableCell className="text-zinc-500 text-sm">
            {milestone ? (
              <span className="flex items-center gap-1">
                <Flag className="w-3 h-3 text-purple-500" />
                {milestone.name}
              </span>
            ) : (
              <span className="text-zinc-400">Unassigned</span>
            )}
          </TableCell>
          <TableCell className="text-zinc-500 text-sm">
            {depTasks.length > 0 ? depTasks.map(d => d.name).join(', ') : '-'}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditTask(task)}
                data-testid={`edit-wbs-task-${task.id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteTask(task.id)}
                className="text-red-600 hover:text-red-700"
                data-testid={`delete-wbs-task-${task.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
        {subtasks.map(subtask => renderTaskRow(subtask, level + 1))}
      </React.Fragment>
    );
  };
  
  // Render milestone-grouped task row (simpler, shows task under milestone header)
  const renderMilestoneTaskRow = (task) => {
    const depTasks = task.dependencies?.map(depId => tasks.find(t => t.id === depId)).filter(Boolean) || [];

    return (
      <TableRow key={task.id} className="hover:bg-zinc-50" data-testid={`milestone-task-${task.id}`}>
        <TableCell className="font-medium pl-10">
          <span className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: task.color || '#f97316' }}
            />
            {task.name}
          </span>
        </TableCell>
        <TableCell>{formatDate(task.start_date)}</TableCell>
        <TableCell>{formatDate(task.end_date)}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <div className="w-16 bg-zinc-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${task.progress}%` }}
              />
            </div>
            <span className="text-sm">{task.progress}%</span>
          </div>
        </TableCell>
        <TableCell className="text-zinc-500 text-sm">
          {depTasks.length > 0 ? depTasks.map(d => d.name).join(', ') : '-'}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditTask(task)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteTask(task.id)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6" data-testid="wbs-manager">
      {/* Prominent Add Task Section */}
      <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-outfit text-2xl font-bold text-zinc-900 mb-1">
              Work Breakdown Structure
            </h2>
            <p className="text-zinc-600">
              Add tasks manually or import from CSV/Excel file
            </p>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="add-task-btn-hero"
            size="lg"
            className="bg-primary hover:bg-orange-600 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Task Manually
          </Button>
        </div>
      </Card>

      {/* Upload Section */}
      <Card className="bg-white border-zinc-200 rounded-xl p-6">
        <h3 className="font-outfit font-semibold text-zinc-900 mb-4">Import WBS from File</h3>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary bg-orange-50'
              : 'border-zinc-300 hover:border-zinc-400'
          }`}
          data-testid="wbs-dropzone"
        >
          <input {...getInputProps()} data-testid="wbs-file-input" />
          <FileSpreadsheet className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <span className="spinner" />
              <span className="text-zinc-600">Importing...</span>
            </div>
          ) : isDragActive ? (
            <p className="text-primary font-medium">Drop the file here...</p>
          ) : (
            <>
              <p className="text-zinc-600 mb-2">
                Drag & drop a CSV or Excel file here, or click to select
              </p>
              <p className="text-xs text-zinc-500">
                Required columns: name, start_date, end_date. Optional: progress, parent_name, dependencies
              </p>
            </>
          )}
        </div>
        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">File Format Guide:</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                <li>Dates should be in YYYY-MM-DD format (e.g., 2025-01-15)</li>
                <li>Use parent_name column to create sub-tasks (must match an existing task name)</li>
                <li>Use dependencies column with comma-separated task names</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Tasks Table */}
      <Card className="bg-white border-zinc-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <h3 className="font-outfit font-semibold text-zinc-900">Tasks ({tasks.length})</h3>
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-48 rounded-lg"
                data-testid="wbs-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {searchQuery && (
              <span className="text-sm text-zinc-500">
                {filteredTasks.length} of {tasks.length} tasks
              </span>
            )}
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'flat' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('flat')}
                className={`rounded-md ${viewMode === 'flat' ? 'bg-white shadow-sm' : ''}`}
                data-testid="view-flat-btn"
              >
                <List className="w-4 h-4 mr-1" />
                Flat View
              </Button>
              <Button
                variant={viewMode === 'milestone' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('milestone')}
                className={`rounded-md ${viewMode === 'milestone' ? 'bg-white shadow-sm' : ''}`}
                data-testid="view-milestone-btn"
              >
                <Layers className="w-4 h-4 mr-1" />
                By Milestone
              </Button>
            </div>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="add-task-btn"
            className="bg-primary hover:bg-orange-600 text-white rounded-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        </div>

        {tasks.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Upload className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
            <p className="mb-4">No tasks yet. Import a WBS file or add tasks manually.</p>
            <Button
              onClick={() => setAddOpen(true)}
              data-testid="add-task-btn-empty"
              className="bg-primary hover:bg-orange-600 text-white rounded-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Task
            </Button>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Search className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
            <p className="mb-4">No tasks match "{searchQuery}"</p>
            <Button
              variant="outline"
              onClick={() => setSearchQuery('')}
              className="rounded-lg"
            >
              Clear Search
            </Button>
          </div>
        ) : viewMode === 'flat' ? (
          // Flat View - Original Table
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task Name</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Milestone</TableHead>
                  <TableHead>Dependencies</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRootTasks.map(task => renderTaskRow(task))}
              </TableBody>
            </Table>
          </div>
        ) : (
          // Milestone-Grouped View
          <div className="space-y-4">
            {milestones.length === 0 && unassignedTasks.length > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-amber-800 text-sm">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                No milestones created yet. Create milestones in the "Milestones" tab to group tasks.
              </div>
            )}
            
            {/* Render each milestone as a collapsible section */}
            {milestones.map(milestone => {
              const milestoneTasks = getTasksByMilestone(milestone.id);
              const isExpanded = expandedMilestones[milestone.id] !== false;
              const completedCount = milestoneTasks.filter(t => t.progress === 100).length;
              const progress = milestoneTasks.length > 0 
                ? Math.round(milestoneTasks.reduce((acc, t) => acc + t.progress, 0) / milestoneTasks.length)
                : 0;
              
              return (
                <div key={milestone.id} className="border border-zinc-200 rounded-xl overflow-hidden" data-testid={`milestone-group-${milestone.id}`}>
                  {/* Milestone Header */}
                  <div
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 cursor-pointer hover:from-purple-100 hover:to-indigo-100 transition-colors"
                    onClick={() => toggleMilestoneExpand(milestone.id)}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-purple-600" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-purple-600" />
                      )}
                      <Flag className="w-5 h-5 text-purple-600" />
                      <div>
                        <h4 className="font-semibold text-zinc-900">{milestone.name}</h4>
                        <p className="text-sm text-zinc-500">
                          Due: {formatDate(milestone.date)} • {milestoneTasks.length} tasks • {completedCount} completed
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-zinc-200 rounded-full h-2">
                          <div
                            className="bg-purple-500 h-2 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-purple-600">{progress}%</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewTask(prev => ({ ...prev, milestone_id: milestone.id }));
                          setAddOpen(true);
                        }}
                        className="border-purple-200 text-purple-600 hover:bg-purple-50"
                        data-testid={`add-task-to-milestone-${milestone.id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Task
                      </Button>
                    </div>
                  </div>
                  
                  {/* Tasks under milestone */}
                  {isExpanded && (
                    <div className="border-t border-zinc-200">
                      {milestoneTasks.length === 0 ? (
                        <div className="p-6 text-center text-zinc-500">
                          <p className="mb-2">No tasks assigned to this milestone yet.</p>
                          <Button
                            size="sm"
                            onClick={() => {
                              setNewTask(prev => ({ ...prev, milestone_id: milestone.id }));
                              setAddOpen(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add First Task
                          </Button>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Task Name</TableHead>
                              <TableHead>Start Date</TableHead>
                              <TableHead>End Date</TableHead>
                              <TableHead>Progress</TableHead>
                              <TableHead>Dependencies</TableHead>
                              <TableHead className="w-[100px]">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {milestoneTasks.map(task => renderMilestoneTaskRow(task))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Unassigned Tasks Section */}
            {unassignedTasks.length > 0 && (
              <div className="border border-zinc-200 rounded-xl overflow-hidden" data-testid="unassigned-tasks-group">
                {/* Unassigned Header */}
                <div
                  className="flex items-center justify-between p-4 bg-zinc-50 cursor-pointer hover:bg-zinc-100 transition-colors"
                  onClick={() => toggleMilestoneExpand('unassigned')}
                >
                  <div className="flex items-center gap-3">
                    {expandedMilestones['unassigned'] !== false ? (
                      <ChevronDown className="w-5 h-5 text-zinc-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-zinc-500" />
                    )}
                    <div>
                      <h4 className="font-semibold text-zinc-700">Unassigned Tasks</h4>
                      <p className="text-sm text-zinc-500">
                        {unassignedTasks.length} tasks not linked to any milestone
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Unassigned Tasks */}
                {expandedMilestones['unassigned'] !== false && (
                  <div className="border-t border-zinc-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Task Name</TableHead>
                          <TableHead>Start Date</TableHead>
                          <TableHead>End Date</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Dependencies</TableHead>
                          <TableHead className="w-[100px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unassignedTasks.map(task => renderMilestoneTaskRow(task))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Add Task Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Add New Task</DialogTitle>
            <DialogDescription>Create a new task with dates, dependencies, and milestone assignment</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTask} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Task Name *</Label>
              <Input
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                placeholder="Enter task name"
                data-testid="add-task-name-input"
                className="rounded-xl h-11"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Start Date</Label>
                <Input
                  type="date"
                  value={newTask.start_date}
                  onChange={(e) => setNewTask({ ...newTask, start_date: e.target.value })}
                  data-testid="add-task-start-input"
                  className="rounded-xl h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">End Date</Label>
                <Input
                  type="date"
                  value={newTask.end_date}
                  onChange={(e) => setNewTask({ ...newTask, end_date: e.target.value })}
                  data-testid="add-task-end-input"
                  className="rounded-xl h-11"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Progress (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={newTask.progress}
                onChange={(e) => setNewTask({ ...newTask, progress: parseInt(e.target.value) || 0 })}
                data-testid="add-task-progress-input"
                className="rounded-xl h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Parent Task (optional)</Label>
              <Select
                value={newTask.parent_id || "none"}
                onValueChange={(val) => setNewTask({ ...newTask, parent_id: val === "none" ? "" : val })}
              >
                <SelectTrigger data-testid="add-task-parent-select" className="rounded-xl h-11">
                  <SelectValue placeholder="No parent (root task)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (root task)</SelectItem>
                  {tasks.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Milestone Assignment */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Flag className="w-4 h-4 text-purple-500" />
                Assign to Milestone (optional)
              </Label>
              <Select
                value={newTask.milestone_id || "none"}
                onValueChange={(val) => setNewTask({ ...newTask, milestone_id: val === "none" ? "" : val })}
              >
                <SelectTrigger data-testid="add-task-milestone-select" className="rounded-xl h-11">
                  <SelectValue placeholder="No milestone (unassigned)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No milestone (unassigned)</SelectItem>
                  {milestones.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="flex items-center gap-2">
                        <Flag className="w-3 h-3 text-purple-500" />
                        {m.name} (Due: {formatDate(m.date)})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {milestones.length === 0 && (
                <p className="text-xs text-zinc-500">
                  No milestones yet. Create milestones in the "Milestones" tab first.
                </p>
              )}
            </div>

            {/* Color Picker */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Task Color
              </Label>
              <div className="flex flex-wrap gap-2">
                {TASK_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setNewTask({ ...newTask, color: color.value })}
                    className={`w-8 h-8 rounded-full transition-all ${
                      newTask.color === color.value 
                        ? 'ring-2 ring-offset-2 ring-zinc-900 scale-110' 
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                    data-testid={`color-${color.name.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>

            {/* Dependencies Selector */}
            {tasks.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Dependencies (tasks that must complete first)</Label>
                <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded-xl p-3 space-y-2 bg-zinc-50">
                  {tasks.map(task => (
                    <label
                      key={task.id}
                      className="flex items-center gap-3 cursor-pointer hover:bg-white p-2 rounded-lg transition-colors"
                    >
                      <Checkbox
                        checked={newTask.dependencies?.includes(task.id) || false}
                        onCheckedChange={() => toggleDependency(task.id, newTask, setNewTask)}
                        data-testid={`dep-checkbox-${task.id}`}
                      />
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: task.color || '#f97316' }}
                      />
                      <span className="text-sm text-zinc-700">{task.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="rounded-lg">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="add-task-submit-btn"
                className="bg-primary hover:bg-orange-600 text-white rounded-lg px-6"
              >
                {loading ? <span className="spinner" /> : 'Add Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={!!editTask} onOpenChange={() => setEditTask(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Edit Task</DialogTitle>
            <DialogDescription>Update task details, dates, dependencies, and milestone</DialogDescription>
          </DialogHeader>
          {editTask && (
            <form onSubmit={handleEditTask} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Task Name *</Label>
                <Input
                  value={editTask.name}
                  onChange={(e) => setEditTask({ ...editTask, name: e.target.value })}
                  data-testid="edit-task-name-input"
                  className="rounded-xl h-11"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Start Date</Label>
                  <Input
                    type="date"
                    value={editTask.start_date}
                    onChange={(e) => setEditTask({ ...editTask, start_date: e.target.value })}
                    data-testid="edit-task-start-input"
                    className="rounded-xl h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">End Date</Label>
                  <Input
                    type="date"
                    value={editTask.end_date}
                    onChange={(e) => setEditTask({ ...editTask, end_date: e.target.value })}
                    data-testid="edit-task-end-input"
                    className="rounded-xl h-11"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Progress (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={editTask.progress}
                  onChange={(e) => setEditTask({ ...editTask, progress: parseInt(e.target.value) || 0 })}
                  data-testid="edit-task-progress-input"
                  className="rounded-xl h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Parent Task</Label>
                <Select
                  value={editTask.parent_id || "none"}
                  onValueChange={(val) => setEditTask({ ...editTask, parent_id: val === "none" ? "" : val })}
                >
                  <SelectTrigger data-testid="edit-task-parent-select" className="rounded-xl h-11">
                    <SelectValue placeholder="No parent (root task)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No parent (root task)</SelectItem>
                    {tasks.filter(t => t.id !== editTask.id).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Milestone Assignment */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Flag className="w-4 h-4 text-purple-500" />
                  Assign to Milestone
                </Label>
                <Select
                  value={editTask.milestone_id || "none"}
                  onValueChange={(val) => setEditTask({ ...editTask, milestone_id: val === "none" ? "" : val })}
                >
                  <SelectTrigger data-testid="edit-task-milestone-select" className="rounded-xl h-11">
                    <SelectValue placeholder="No milestone (unassigned)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No milestone (unassigned)</SelectItem>
                    {milestones.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        <span className="flex items-center gap-2">
                          <Flag className="w-3 h-3 text-purple-500" />
                          {m.name} (Due: {formatDate(m.date)})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Color Picker */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Task Color
                </Label>
                <div className="flex flex-wrap gap-2">
                  {TASK_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setEditTask({ ...editTask, color: color.value })}
                      className={`w-8 h-8 rounded-full transition-all ${
                        editTask.color === color.value 
                          ? 'ring-2 ring-offset-2 ring-zinc-900 scale-110' 
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Dependencies Selector */}
              {tasks.filter(t => t.id !== editTask.id).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Dependencies (tasks that must complete first)</Label>
                  <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded-xl p-3 space-y-2 bg-zinc-50">
                    {tasks.filter(t => t.id !== editTask.id).map(task => (
                      <label
                        key={task.id}
                        className="flex items-center gap-3 cursor-pointer hover:bg-white p-2 rounded-lg transition-colors"
                      >
                        <Checkbox
                          checked={editTask.dependencies?.includes(task.id) || false}
                          onCheckedChange={() => toggleDependency(task.id, editTask, setEditTask)}
                        />
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: task.color || '#f97316' }}
                        />
                        <span className="text-sm text-zinc-700">{task.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setEditTask(null)} className="rounded-lg">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="edit-task-submit-btn"
                  className="bg-primary hover:bg-orange-600 text-white rounded-lg px-6"
                >
                  {loading ? <span className="spinner" /> : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
